/** Map SoftwareResponseDto → SoftwareRequestDto for nested updates */
import { buildDeploymentStatuses } from './wizardPayloadBuilder';

const mapDataSources = (sources = []) =>
  sources.map((ds) => ({
    id: ds.id,
    sourceName: ds.sourceName,
    description: ds.description,
    dataInformations: (ds.dataInformations ?? []).map((di) => ({
      id: di.id,
      personalData: di.personalData,
      trainingDataOrigin: di.trainingDataOrigin,
      modelType: di.modelType,
      sensitiveDataCategories: di.sensitiveDataCategories ?? [],
    })),
  }));

const mapDocuments = (documents = []) =>
  documents.map((doc) => ({
    id: doc.id,
    documentName: doc.documentName,
    fileName: doc.fileName,
    contentType: doc.contentType,
    documentType: doc.documentType,
    deploymentStatus: doc.deploymentStatus,
    location: doc.location,
  }));

const mapIncidents = (incidents = []) =>
  incidents.map((i) => ({
    id: i.id,
    incidentTicket: i.incidentTicket,
    incidentResponseOwner: i.incidentResponseOwner,
    knownIncidents: i.knownIncidents,
    incidentDetails: i.incidentDetails,
    escalationPathDefined: i.escalationPathDefined,
    incidentDate: i.incidentDate,
    reportedAt: i.reportedAt,
    solvedApprovedAt: i.solvedApprovedAt,
    riskRegisterIds: Array.isArray(i.riskRegisterIds)
      ? i.riskRegisterIds
      : i.riskRegisterId != null
        ? [i.riskRegisterId]
        : [],
    severityLevel: i.severityLevel || i.riskPriority,
    rootCause: i.rootCause,
    resolutionStatus: i.resolutionStatus,
    actionTaken: i.actionTaken,
    reportedBy: i.reportedBy,
    incidentReportingStaff: i.incidentReportingStaff,
    approvals: (i.approvals ?? []).map((a) => ({
      id: a.id,
      reviewId: a.reviewId,
      monitoringReviewId: a.monitoringReviewId,
      incidentId: a.incidentId ?? i.id,
      approverName: a.approverName,
      approverRole: a.approverRole,
      approverCommittee: a.approverCommittee,
      approvalDate: a.approvalDate,
      approvalStatus: a.approvalStatus,
      approvalComments: a.approvalComments,
      approvalLevel: a.approvalLevel,
      signatureConfirmation: a.signatureConfirmation,
      jobPosition: a.jobPosition ?? a.job_position ?? '',
    })),
  }));

const mapChangeManagements = (items = []) =>
  items.map((c) => ({
    id: c.id,
    title: c.title,
    description: c.description,
    changeType: c.changeType,
    priority: c.priority,
    riskLevel: c.riskLevel,
    reasonForChange: c.reasonForChange,
    requestedBy: c.requestedBy,
    requestedDate: c.requestedDate,
    currentVersion: c.currentVersion,
    targetVersion: c.targetVersion,
    implementationPlan: c.implementationPlan,
    rollbackPlan: c.rollbackPlan,
    testPlan: c.testPlan,
    testResult: c.testResult,
    scheduledStart: c.scheduledStart,
    scheduledEnd: c.scheduledEnd,
    actualStart: c.actualStart,
    actualEnd: c.actualEnd,
    downtimeRequired: c.downtimeRequired,
    downtimeDuration: c.downtimeDuration,
    implementationStatus: c.implementationStatus,
    implementedBy: c.implementedBy,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    approvals: (c.approvals ?? []).map((a) => ({
      id: a.id,
      changeManagementId: a.changeManagementId ?? c.id,
      approverName: a.approverName,
      approverRole: a.approverRole,
      approverCommittee: a.approverCommittee,
      approvalDate: a.approvalDate,
      approvalStatus: a.approvalStatus,
      approvalComments: a.approvalComments,
      approvalLevel: a.approvalLevel,
      signatureConfirmation: a.signatureConfirmation,
      jobPosition: a.jobPosition ?? a.job_position ?? '',
    })),
  }));

const mapIntegrations = (integrations = []) =>
  integrations.map((i) => ({
    id: i.id,
    sourceSystem: i.sourceSystem,
    targetSystem: i.targetSystem,
    integrationType: i.integrationType,
    integrationPurpose: i.integrationPurpose,
    dataFlowDirection: i.dataFlowDirection,
    dataExchanged: i.dataExchanged,
    integrationMethod: i.integrationMethod,
    authenticationMethod: i.authenticationMethod,
    integrationOwner: i.integrationOwner,
    securityMeasures: i.securityMeasures,
    integrationStatus: i.integrationStatus,
  }));

const mapReviews = (reviews = []) =>
  reviews.map((r) => ({
    id: r.id,
    lastReviewDate: r.lastReviewDate,
    nextReviewDate: r.nextReviewDate,
    reviewedBy: r.reviewedBy,
    reviewerRoleCommittee: r.reviewerRoleCommittee,
    performanceMonitoring: r.performanceMonitoring,
    driftMonitoring: r.driftMonitoring,
    reviewComments: r.reviewComments,
    approvals: (r.approvals ?? []).map((a) => ({
      id: a.id,
      reviewId: a.reviewId ?? r.id,
      approverName: a.approverName,
      approverRole: a.approverRole,
      approverCommittee: a.approverCommittee,
      approvalDate: a.approvalDate,
      approvalStatus: a.approvalStatus,
      approvalComments: a.approvalComments,
      approvalLevel: a.approvalLevel,
      signatureConfirmation: a.signatureConfirmation,
      jobPosition: a.jobPosition ?? a.job_position ?? '',
    })),
  }));

const mapMonitoringReviews = (items = []) =>
  items.map((item) => ({
    id: item.linkId ?? null,
    monitoringReviewId: item.monitoringReviewId ?? item.id,
    monitoringType: item.monitoringType,
    reviewAreaControl: item.reviewAreaControl,
    standardControlReference: item.standardControlReference,
    reviewFrequency: item.reviewFrequency,
    observationFindings: item.observationFindings,
    riskRating: item.riskRating,
    correctiveActionRequired: item.correctiveActionRequired,
    actionOwnerEmail: item.actionOwnerEmail,
    status: item.status,
    nextReviewDate: item.nextReviewDate,
    evidenceFileName: item.evidenceFileName,
    evidenceContentType: item.evidenceContentType,
    evidenceLocation: item.evidenceLocation,
    reviewer: item.reviewer,
    approvals: (item.approvals ?? []).map((a) => ({
      id: a.id,
      reviewId: a.reviewId,
      monitoringReviewId: a.monitoringReviewId ?? item.monitoringReviewId ?? item.id,
      approverName: a.approverName,
      approverRole: a.approverRole,
      approverCommittee: a.approverCommittee,
      approvalDate: a.approvalDate,
      approvalStatus: a.approvalStatus,
      approvalComments: a.approvalComments,
      approvalLevel: a.approvalLevel,
      signatureConfirmation: a.signatureConfirmation,
      jobPosition: a.jobPosition ?? a.job_position ?? '',
    })),
  }));

const mapSoftwareRisks = (items = []) =>
  items.map((item) => ({
    id: item.id,
    riskRegisterId: item.riskRegisterId,
    riskCategory: item.riskCategory,
    resourcesImpacted: item.resourcesImpacted,
    riskName: item.riskName,
    riskPriority: item.riskPriority,
    riskDescription: item.riskDescription,
    riskOwner: item.riskOwner,
    impact: item.impact,
    causes: item.causes,
    controlsAndMitigation: item.controlsAndMitigation,
    status: item.status,
  }));

const mapApprovals = (approvals = []) =>
  approvals.map((a) => ({
    id: a.id,
    reviewId: a.reviewId,
    monitoringReviewId: a.monitoringReviewId,
    incidentId: a.incidentId,
    approverName: a.approverName,
    approverRole: a.approverRole,
    approverCommittee: a.approverCommittee,
    approvalDate: a.approvalDate,
    approvalStatus: a.approvalStatus,
    approvalComments: a.approvalComments,
    approvalLevel: a.approvalLevel,
    signatureConfirmation: a.signatureConfirmation,
      jobPosition: a.jobPosition ?? a.job_position ?? '',
      reviewedBy: a.reviewedBy ?? '',
      committee: a.committee ?? '',
      softwareId: a.softwareId ?? null,
      softwareMonitoringAndReviewId: a.softwareMonitoringAndReviewId ?? null,
  }));

const mapDeploymentStatuses = (stages = []) =>
  stages.map((stage) => ({
    id: stage.id,
    status: stage.status,
    stageDate: stage.stageDate,
    current: Boolean(stage.current),
    approvals: (stage.approvals ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      email: a.email,
      approvedAt: a.approvedAt,
      decision: a.decision,
      comment: a.comment,
    })),
  }));

export function softwareToRequest(detail) {
  if (!detail) return null;

  const dataInformation = detail.dataInformation
    ? {
        id: detail.dataInformation.id,
        personalData: detail.dataInformation.personalData,
        trainingDataOrigin: detail.dataInformation.trainingDataOrigin,
        modelType: detail.dataInformation.modelType,
        sensitiveDataCategories: detail.dataInformation.sensitiveDataCategories ?? [],
      }
    : null;

  const riskAssessment = detail.riskAssessment
    ? {
        knownBias: detail.riskAssessment.knownBias,
        biasDescription: detail.riskAssessment.biasDescription,
        usesArtificialIntelligence: detail.riskAssessment.usesArtificialIntelligence,
      }
    : null;

  return {
    softwareName: detail.softwareName,
    internalReference: detail.internalReference,
    externalOrganisationReference: detail.externalOrganisationReference,
    contractStartDate: detail.contractStartDate,
    contractEndDate: detail.contractEndDate,
    contractDuration: detail.contractDuration,
    contractDurationUnit: detail.contractDurationUnit,
    licenseStartDate: detail.licenseStartDate,
    licenseEndDate: detail.licenseEndDate,
    licenseDuration: detail.licenseDuration,
    licenseDurationUnit: detail.licenseDurationUnit,
    deploymentEnvironment: detail.deploymentEnvironment,
    systemIpAddress: detail.systemIpAddress,
    systemUrl: detail.systemUrl,
    hostingServerName: detail.hostingServerName,
    deploymentStatus: detail.deploymentStatus,
    decisionType: detail.decisionType,
    firstDeployedDate: detail.firstDeployedDate,
    deploymentStatusDates: detail.deploymentStatusDates ?? undefined,
    primaryPurpose: detail.primaryPurpose,
    businessProcessSupported: detail.businessProcessSupported,
    humanOversight: detail.humanOversight,
    humanOversightExplanation: detail.humanOversightExplanation,
    businessUnitIds: (detail.businessUnits ?? []).map((b) => b.id),
    systemOwnerIds: (detail.systemOwners ?? []).map((o) => o.id),
    vendorIds: (detail.vendors ?? []).map((v) => v.id),
    regulationTypes: detail.regulationTypes ?? [],
    dataInformation,
    riskAssessment,
    dataSources: mapDataSources(detail.dataSources),
    documents: mapDocuments(detail.documents),
    incidents: mapIncidents(detail.incidents),
    softwareRisks: mapSoftwareRisks(detail.softwareRisks),
    changeManagements: mapChangeManagements(detail.changeManagements),
    integratedWithOtherSystems: detail.integratedWithOtherSystems,
    integrations: mapIntegrations(detail.integrations),
    reviews: mapReviews(detail.reviews),
    monitoringReviews: mapMonitoringReviews(detail.monitoringReviews),
    approvals: mapApprovals(detail.approvals),
    deploymentStatuses: mapDeploymentStatuses(detail.deploymentStatuses),
  };
}

/** Core software fields only — preserves nested collections from existing detail. */
export function mergeCoreFields(detail, coreValues) {
  const base = softwareToRequest(detail) ?? {};
  const dates = coreValues.deploymentStatusDates;
  return {
    ...base,
    softwareName: coreValues.softwareName?.trim(),
    // Internal ID is system-assigned — keep existing value from detail.
    externalOrganisationReference:
      coreValues.externalOrganisationReference?.trim() || undefined,
    contractStartDate: coreValues.contractStartDate || undefined,
    contractEndDate: coreValues.contractEndDate || undefined,
    contractDuration:
      coreValues.contractDuration === '' || coreValues.contractDuration == null
        ? undefined
        : Number(coreValues.contractDuration),
    contractDurationUnit: coreValues.contractDurationUnit || undefined,
    licenseStartDate: coreValues.licenseStartDate || undefined,
    licenseEndDate: coreValues.licenseEndDate || undefined,
    licenseDuration:
      coreValues.licenseDuration === '' || coreValues.licenseDuration == null
        ? undefined
        : Number(coreValues.licenseDuration),
    licenseDurationUnit: coreValues.licenseDurationUnit || undefined,
    deploymentEnvironment: coreValues.deploymentEnvironment || undefined,
    systemIpAddress: coreValues.systemIpAddress?.trim() || undefined,
    systemUrl: coreValues.systemUrl?.trim() || undefined,
    hostingServerName: coreValues.hostingServerName?.trim() || undefined,
    deploymentStatus: coreValues.deploymentStatus,
    decisionType: coreValues.decisionType || undefined,
    firstDeployedDate: dates?.production || coreValues.firstDeployedDate || undefined,
    deploymentStatusDates: dates
      ? {
          planned: dates.planned || undefined,
          development: dates.development || undefined,
          testing: dates.testing || undefined,
          pilot: dates.pilot || undefined,
          production: dates.production || undefined,
          retired: dates.retired || undefined,
          suspended: dates.suspended || undefined,
        }
      : base.deploymentStatusDates,
    deploymentStatuses: buildDeploymentStatuses(coreValues),
    primaryPurpose: coreValues.primaryPurpose?.trim() || undefined,
    businessProcessSupported: coreValues.businessProcessSupported?.trim() || undefined,
    humanOversight: Boolean(coreValues.humanOversight),
    humanOversightExplanation: coreValues.humanOversightExplanation?.trim() || undefined,
    businessUnitIds: (coreValues.businessUnitIds ?? []).map(Number),
    systemOwnerIds: (coreValues.systemOwnerIds ?? []).map(Number),
    vendorIds: (coreValues.vendorIds ?? []).map(Number).filter(Boolean),
    regulationTypes: coreValues.regulationTypes ?? base.regulationTypes ?? [],
  };
}

export function joinNames(items, labelKey = 'name') {
  if (!items?.length) return '';
  return items.map((i) => i[labelKey]).filter(Boolean).join(', ');
}
